import * as React from 'react';
import type { SVGProps } from 'react';
import OtIcon from './OtIcon';

/**
 * Illustrated family — the marks that stand for a state rather than for a thing.
 *
 * These are the icons a dashboard tile, a calm state or a section rail puts next to a number. They
 * are marks, not controls: an `Edit` or a `Close` stays Material, because it labels an action and
 * lives inside a button.
 *
 * Lighting model and construction rules live in {@link OtIcon}.
 */

type TProps = SVGProps<SVGSVGElement>;

const Spec = ({ d, o = 0.92, w = 2.6 }: { d: string; o?: number; w?: number }) => (
	<path d={d} stroke='#fff' strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

const Bounce = ({ d, c, o = 0.5, w = 2 }: { d: string; c: string; o?: number; w?: number }) => (
	<path d={d} stroke={c} strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

/* ------------------------------------------------------------------ Bolt */
/** Energy: an AI credit, a fast action, a suggestion the product offers. */
export const OtBolt = (props: TProps) => (
	<OtIcon slug='bolt' palette='gold' groundX={44} groundY={80} groundR={16} {...props}>
		{(r) => (
			<>
				<path d='M52 8L20 50h18l-6 38 32-44H46z' fill={r.fill} />
				<path
					d='M52 8L20 50h18l-6 38 32-44H46z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
					strokeLinejoin='round'
				/>
				{/* the fold down the middle: the bolt is a bent plate, not a flat cut-out */}
				<path d='M38 50h8l-6 -8' fill={r.tone.occlusion} fillOpacity='.4' />
				<Spec d='M46 14L26 40' o={0.75} w={2.4} />
				<Bounce d='M56 52l-14 20' c='#ffd7a0' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ CheckCircle */
/** Everything is in order — the calm state, not a celebration. */
export const OtCheckCircle = (props: TProps) => (
	<OtIcon slug='check-circle' palette='green' groundX={46} groundY={80} groundR={22} {...props}>
		{(r) => (
			<>
				<circle cx='46' cy='42' r='32' fill={r.fill} />
				<circle cx='46' cy='42' r='32' fill='none' stroke={r.rim} strokeWidth='3.6' />
				<circle
					cx='46'
					cy='42'
					r='29.2'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.42'
					strokeWidth='1.6'
				/>
				{/* the tick is engraved: a shadow stroke under a lit one */}
				<path
					d='M32 43l10 10 20-21'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.5'
					strokeWidth='9'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<path
					d='M31.2 42.2l10 10 20-21'
					fill='none'
					stroke='#fff'
					strokeWidth='6'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<Spec d='M27 28a30 30 0 0 1 13-9' o={0.8} />
				<Bounce d='M67 60a30 30 0 0 1-11 8' c={r.tone.bounce} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Warning */
/** Something needs a decision. Amber, never red: red is for what already failed. */
export const OtWarning = (props: TProps) => (
	<OtIcon slug='warning' palette='orange' groundX={46} groundY={78} groundR={26} {...props}>
		{(r) => (
			<>
				<path d='M46 8a9 9 0 0 1 7.8 4.5l32 56A9 9 0 0 1 78 82H14a9 9 0 0 1-7.8-13.5l32-56A9 9 0 0 1 46 8z' fill={r.fill} />
				<path
					d='M46 8a9 9 0 0 1 7.8 4.5l32 56A9 9 0 0 1 78 82H14a9 9 0 0 1-7.8-13.5l32-56A9 9 0 0 1 46 8z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.6'
					strokeLinejoin='round'
				/>
				<path
					d='M46 30v22'
					stroke={r.tone.occlusion}
					strokeOpacity='.5'
					strokeWidth='9'
					strokeLinecap='round'
				/>
				<path d='M45.2 29.2v22' stroke='#fff' strokeWidth='6' strokeLinecap='round' />
				<circle cx='46' cy='65' r='5.4' fill={r.tone.occlusion} fillOpacity='.5' />
				<circle cx='45.2' cy='64.2' r='4.4' fill='#fff' />
				<Spec d='M28 44L40 22' o={0.8} />
				<Bounce d='M70 62l-8-14' c='#ffd7a0' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Upgrade */
/** The plan can go further: the paywall's own mark. */
export const OtUpgrade = (props: TProps) => (
	<OtIcon slug='upgrade' palette='violet' groundX={46} groundY={80} groundR={24} {...props}>
		{(r) => (
			<>
				{/* three steps rising to the right */}
				<rect x='12' y='54' width='20' height='20' rx='6' fill={r.fill} fillOpacity='.55' />
				<rect
					x='12'
					y='54'
					width='20'
					height='20'
					rx='6'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.6'
				/>
				<rect x='36' y='40' width='20' height='34' rx='6' fill={r.fill} fillOpacity='.8' />
				<rect
					x='36'
					y='40'
					width='20'
					height='34'
					rx='6'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.6'
				/>
				<rect x='60' y='22' width='20' height='52' rx='6' fill={r.fill} />
				<rect
					x='60'
					y='22'
					width='20'
					height='52'
					rx='6'
					fill='none'
					stroke={r.rim}
					strokeWidth='3'
				/>
				{/* the arrow that says this is a climb, not a chart */}
				<path
					d='M22 40L46 20l24-8'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.45'
					strokeWidth='7'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<path
					d='M21 39L45 19l24-8'
					fill='none'
					stroke='#fff'
					strokeWidth='4.4'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<path
					d='M58 8h13v13'
					fill='none'
					stroke='#fff'
					strokeWidth='4.4'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<Spec d='M64 30a6 6 0 0 1 5-4' o={0.85} w={2.4} />
				<Bounce d='M76 62v6' c='#7fe6d2' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Sensors */
/** Something is being listened for: a signal picked up before it has a name. */
export const OtSensors = (props: TProps) => (
	<OtIcon slug='sensors' palette='cyan' groundX={46} groundY={80} groundR={16} {...props}>
		{(r) => (
			<>
				{/* the waves fan out from the core, faintest furthest away */}
				<path
					d='M24 20a34 34 0 0 0 0 44M68 20a34 34 0 0 1 0 44'
					fill='none'
					stroke={r.rim}
					strokeWidth='5'
					strokeLinecap='round'
				/>
				<path
					d='M35 30a19 19 0 0 0 0 24M57 30a19 19 0 0 1 0 24'
					fill='none'
					stroke={r.rim}
					strokeOpacity='.75'
					strokeWidth='4.4'
					strokeLinecap='round'
				/>
				<circle cx='46' cy='42' r='11' fill={r.fill} />
				<circle cx='46' cy='42' r='11' fill='none' stroke={r.rim} strokeWidth='3' />
				<circle cx='42.5' cy='38.5' r='3.4' fill='#fff' fillOpacity='.85' />
				<Bounce d='M62 60a19 19 0 0 1-6 6' c={r.tone.bounce} o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Help */
/** A question: the explainer a card offers when the number needs one. */
export const OtHelp = (props: TProps) => (
	<OtIcon slug='help' palette='blue' groundX={46} groundY={80} groundR={22} {...props}>
		{(r) => (
			<>
				<circle cx='46' cy='42' r='32' fill={r.fill} />
				<circle cx='46' cy='42' r='32' fill='none' stroke={r.rim} strokeWidth='3.6' />
				<circle
					cx='46'
					cy='42'
					r='29.2'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.42'
					strokeWidth='1.6'
				/>
				{/* the mark is engraved into the face, like the tick on OtCheckCircle */}
				<path
					d='M37 33a9.5 9.5 0 0 1 18 4c0 6-9 7-9 13'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.5'
					strokeWidth='9'
					strokeLinecap='round'
				/>
				<path
					d='M36.2 32.2a9.5 9.5 0 0 1 18 4c0 6-9 7-9 13'
					fill='none'
					stroke='#fff'
					strokeWidth='6'
					strokeLinecap='round'
				/>
				<circle cx='46' cy='60' r='5' fill={r.tone.occlusion} fillOpacity='.5' />
				<circle cx='45.2' cy='59.2' r='4' fill='#fff' />
				<Spec d='M27 28a30 30 0 0 1 13-9' o={0.8} />
				<Bounce d='M67 60a30 30 0 0 1-11 8' c='#7fe6d2' />
			</>
		)}
	</OtIcon>
);
